import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Button,
  Empty,
  Row,
  Col,
  Progress,
  Statistic,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  StarFilled,
  AppstoreOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { OntologyNode } from '../hooks/useResourceTree';
import { apiClient } from '@packages/api-client';
import { StatisticsApi, type OverviewStatisticsVo } from '../api';

const { Title, Text } = Typography;

interface OverviewProps {
  hasOntologies: boolean;
  onAddOntology: () => void;
}

export const Overview: React.FC<OverviewProps> = ({
  hasOntologies,
  onAddOntology,
}) => {
  // 状态管理
  const [statistics, setStatistics] = useState<OverviewStatisticsVo>({
    countPublishedOntology: 0,
    countOntologyObjects: 0,
  });
  const [loading, setLoading] = useState(false);

  // 获取当前登录用户信息
  const currentUser = apiClient.getCurrentUser();
  const username = currentUser?.username || '用户';

  // 获取当前日期
  const currentDate = new Date().toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  // 加载统计数据
  useEffect(() => {
    const fetchStatistics = async () => {
      if (!hasOntologies) return;

      try {
        setLoading(true);
        const data = await StatisticsApi.getOverviewStatistics();
        setStatistics(data);
      } catch (error) {
        console.error('获取统计数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [hasOntologies]);

  // 如果只有目录没有本体，显示暂无本体页面
  if (!hasOntologies) {
    return (
      <div className="h-full min-h-0 flex flex-col">
        {/* 头部标题区域 */}
        <div className="shrink-0 flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-200">
          <Title level={3} className="!mb-0">
            总览
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAddOntology}
          >
            新建本体
          </Button>
        </div>

        {/* 暂无本体的空状态 */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
          <div className="h-full flex items-center justify-center">
            <Empty
              image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
              // antd Empty 新版样式配置（替代 imageStyle）
              styles={{ image: { height: 120 } }}
              description={
                <div className="text-center">
                  <Text className="text-lg text-gray-600 block mb-2">
                    暂无本体
                  </Text>
                  <Text type="secondary" className="text-sm">
                    您还没有创建任何本体，请点击上方按钮或下方按钮开始创建
                  </Text>
                </div>
              }
            >
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={onAddOntology}
              >
                创建第一个本体
              </Button>
            </Empty>
          </div>
        </div>
      </div>
    );
  }

  // 有本体时显示正常的总览页面
  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* 头部标题区域 */}
      <div className="shrink-0 flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-200">
        <Title level={3} className="!mb-0">
          总览
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={onAddOntology}>
          新建本体
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
        {/* 头部欢迎区域 */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Title level={2} className="!mb-1">
                你好，{username} <StarFilled className="text-yellow-400 ml-2" />
              </Title>
              <Text type="secondary">
                欢迎使用 IntelliX 本体管理系统，让我们开始探索知识的旅程
              </Text>
            </div>
            <div className="text-gray-400 text-sm">{currentDate}</div>
          </div>
        </div>

        {/* 统计卡片 */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col span={12}>
            <Card className="!bg-blue-50 !border-blue-200">
              <Spin spinning={loading}>
                <div className="flex items-center justify-between">
                  <div>
                    <Text type="secondary" className="text-sm block mb-1">
                      本体对象数量
                    </Text>
                    <div className="flex items-center space-x-2">
                      <AppstoreOutlined className="text-blue-500 text-lg" />
                      <Statistic
                        value={statistics.countOntologyObjects}
                        className="!mb-0"
                        valueStyle={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: '#1890ff',
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-right"></div>
                </div>
              </Spin>
            </Card>
          </Col>
          <Col span={12}>
            <Card className="!bg-blue-50 !border-blue-200">
              <Spin spinning={loading}>
                <div className="flex items-center justify-between">
                  <div>
                    <Text type="secondary" className="text-sm block mb-1">
                      已发表本体数量
                    </Text>
                    <div className="flex items-center space-x-2">
                      <MessageOutlined className="text-blue-500 text-lg" />
                      <Statistic
                        value={statistics.countPublishedOntology}
                        className="!mb-0"
                        valueStyle={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: '#1890ff',
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-right"></div>
                </div>
              </Spin>
            </Card>
          </Col>
        </Row>

        {/* 本体挂载分布图 */}
        <Card className="!border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <Title level={4} className="!mb-0">
              本体挂载分布
            </Title>
            <Text type="secondary" className="text-sm">
              最近 30 天
            </Text>
          </div>

          <div className="flex items-center justify-center py-8">
            <Empty
              image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
              // antd Empty 新版样式配置（替代 imageStyle）
              styles={{ image: { height: 120 } }}
              description={
                <div className="text-center">
                  <Text className="text-lg text-gray-600 block mb-2">
                    暂无挂载
                  </Text>
                  <Text type="secondary" className="text-sm">
                    当前没有本体挂载数据
                  </Text>
                </div>
              }
            />
          </div>
        </Card>
      </div>
    </div>
  );
};
